import { useMemo } from "react";
import uniq from "lodash/uniq";
import { UIFlightData } from "@flights/types";
import { useI18n } from "@bookingcom/lingojs-react";
import usePointOfSale from "../../../hooks/usePointOfSale";
import { useSelector } from "react-redux";
import { getAncillaries } from "../../../store/flightDetails/selectors";
import {
  isFeatureRunningClientSide,
  localizationUtils,
  priceBreakdownUtils,
  pricingUtils,
  ryanairUtils
} from "@flights/web-api-utils-universal";
import { type TicketType } from "../../../ancillaries/components/TicketTypeSelector/types";
import { transformDisclaimersForExtras, transformDisclaimersForPriceBreakdown } from "./transforms/disclaimers";
import { getExtraProducts } from "../../../ancillaries/store/extraProducts/selectors";
import { type BreakdownProps } from "./Breakdown/Breakdown";
import useRouteName from "../../../hooks/useRouteName";
import useEvents from "../../../hooks/useEvents";
import isEmpty from "lodash/isEmpty";

export type UnifiedPriceBreakdownTicketTypeControllerProps = {
  flight?: UIFlightData;
  type: TicketType;
};

type CopyFuncParams = Parameters<typeof priceBreakdownUtils.getExtraCopy>;
type POSType = CopyFuncParams[3]["pointOfSale"];

export function useUnifiedPriceBreakdownTicketTypeController(props: UnifiedPriceBreakdownTicketTypeControllerProps) {
  const { flight, type } = props;
  const i18n = useI18n();
  const ipCountry = usePointOfSale();
  const routeName = useRouteName();
  const { squeak } = useEvents();
  const ancillaries = useSelector(getAncillaries);
  const extraProducts = useSelector(getExtraProducts);
  const segments = useMemo(() => flight?.segments, [flight]);
  const isHungaryDepartingRyanAir = useMemo(
    () => localizationUtils.isHungaryDepartingRyanAirFlight(segments),
    [segments]
  );

  const baseOptions = useMemo(
    () => ({
      pointOfSale: (ipCountry || "") as POSType,
      isFlightFromRyanAirHungary: isHungaryDepartingRyanAir,
      segments: segments || []
    }),
    [ipCountry, isHungaryDepartingRyanAir, segments]
  );

  const unifiedPriceBreakdown = useMemo(() => {
    const upb = flight?.unifiedPriceBreakdown;
    if (isFeatureRunningClientSide("FLIGHTS_PRICING_ENABLE_PRODUCT_ID_SQUEAK") && upb && !upb.id) {
      squeak(
        `UnifiedPriceTag::useUnifiedPriceBreakdownTicketTypeController - product id is missing - route "${routeName}"`
      );
    }

    return upb;
  }, [flight, routeName, squeak]);

  const allAncillaries = useMemo(() => {
    return !isEmpty(extraProducts) ? extraProducts : ancillaries;
  }, [ancillaries, extraProducts]);

  const extras = useMemo(() => {
    if (type === "cfar" && allAncillaries?.cancelForAnyReason) {
      const { priceBreakdown, airProductReference } = allAncillaries.cancelForAnyReason;

      return {
        cancelForAnyReason: {
          price: priceBreakdown,
          reference: airProductReference
        }
      };
    } else if (type === "flexible" && allAncillaries?.flexibleTicket) {
      const { travellers, priceBreakdown, airProductReference } = allAncillaries.flexibleTicket;
      const price = pricingUtils.sumPriceBreakdown(new Array(travellers.length).fill(priceBreakdown));

      return {
        flexibleTicket: {
          price,
          reference: airProductReference,
          travelerPrices: []
        }
      };
    }

    return {};
  }, [type, allAncillaries]);

  const addedItems = useMemo(() => {
    return priceBreakdownUtils.transformUnifiedPriceBreakdownItemForExtra(i18n, extras, baseOptions);
  }, [i18n, extras, baseOptions]);

  const disclaimers = useMemo(() => {
    return uniq([
      ...transformDisclaimersForPriceBreakdown(flight?.priceBreakdown),
      ...transformDisclaimersForExtras(extras)
    ]);
  }, [flight, extras]);

  const isRyanair = useMemo(() => ryanairUtils.isAnyRyanairSegments(segments), [segments]);

  const expandedByDefault = useMemo<BreakdownProps["expandedByDefault"]>(() => {
    return isRyanair
      ? {
          FLIGHT_ADULT: true,
          FLIGHT_CHILD: true,
          FLIGHT_INFANT: true,
          EXTRA: true
        }
      : undefined;
  }, [isRyanair]);

  return {
    unifiedPriceBreakdown,
    isHungaryDepartingRyanAir,
    expandedByDefault,
    disclaimers,
    addedItems
  };
}
